import { embedHost } from './platformEmbed';
import { useDataStore } from '@/stores/dataStore';
import { getDmCall, subscribeDmCall } from './dmCall';
import { getRecorder, subscribeRecorder } from './voiceRecorder';
import { sendQueue, failedSends } from '@/components/contentPane/composerQueue';
import { flushDraft } from './drafts';

export const WORKSPACE_RESTORE_TIMEOUT_MS = 10_000;

export interface WorkspaceSelection { guildId: string | null; channelId: string | null }
const id = (value: unknown): value is string | null => value === null || (typeof value === 'string' && /^[a-zA-Z0-9_-]{1,128}$/.test(value));

export function readWorkspaceSelection(value: unknown): WorkspaceSelection | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const s = value as WorkspaceSelection;
  return id(s.guildId) && id(s.channelId) ? { guildId: s.guildId, channelId: s.channelId } : null;
}

/** The destination must already be visible to this account; a pointer grants no access. */
export function restoreWorkspace(selection: WorkspaceSelection): boolean {
  const s = useDataStore.getState();
  if (selection.guildId) {
    const guild = s.guilds[selection.guildId];
    if (!guild || (selection.channelId && !guild.channels.some(c => c.id === selection.channelId))) return false;
    s.selectGuild(guild.id);
    s.selectChannel(selection.channelId);
  } else if (selection.channelId) {
    if (!s.dmChannels[selection.channelId]) return false;
    s.selectDirectChannel(selection.channelId);
  } else s.goHome();
  return true;
}

export function canMoveWorkspace(): boolean {
  const s = useDataStore.getState();
  return !s.myVoiceChannelId && getDmCall().kind === 'idle' && getRecorder().kind !== 'recording'
    && !failedSends.hasItems() && !sendQueue.hasPending()
    && !document.querySelector('[data-bunker-window-busy="true"]');
}

/** Only a bounded workspace pointer and movement status cross this bridge. */
export function installWorkspaceWindowBridge() {
  const host = embedHost();
  if (!host) return () => {};
  let previous: (WorkspaceSelection & { canMove: boolean }) | null = null;
  let publishTimer: ReturnType<typeof setTimeout> | null = null;
  let restoreTimer: ReturnType<typeof setTimeout> | null = null;
  let restoreDeadline = 0;
  let restorePending: WorkspaceSelection | null = null;
  const clearRestore = () => {
    restorePending = null;
    if (restoreTimer !== null) clearTimeout(restoreTimer);
    restoreTimer = null;
  };
  const publish = (requestId?: string) => {
    if (publishTimer !== null) clearTimeout(publishTimer);
    publishTimer = null;
    const { currentGuildId: guildId, currentChannelId: channelId } = useDataStore.getState();
    const canMove = canMoveWorkspace();
    if (!requestId && previous?.guildId === guildId && previous.channelId === channelId && previous.canMove === canMove) return;
    previous = { guildId, channelId, canMove };
    if (requestId && channelId) flushDraft(channelId);
    window.parent.postMessage({ v: 1, type: 'cispracs:bunker-workspace', guildId, channelId, canMove, ...(requestId ? { requestId } : {}) }, host);
  };
  // A timer also runs in a hidden persistent frame; RAF may be suspended there.
  // Fresh handoff requests bypass batching and recheck all guards immediately.
  const schedule = () => { if (publishTimer === null) publishTimer = setTimeout(() => publish(), 0); };
  const restore = () => {
    const selection = restorePending;
    if (!selection) return;
    if (Date.now() >= restoreDeadline) { clearRestore(); return; }
    restorePending = null; // Store actions below may notify subscribers synchronously.
    if (restoreWorkspace(selection)) clearRestore();
    else restorePending = selection;
  };
  const offData = useDataStore.subscribe((state, prev) => {
    const selectionChanged = state.currentGuildId !== prev.currentGuildId || state.currentChannelId !== prev.currentChannelId;
    // User navigation wins over a not-yet-hydrated pointer from the old window.
    if (selectionChanged) clearRestore();
    if (restorePending && (state.guilds !== prev.guilds || state.dmChannels !== prev.dmChannels)) restore();
    if (selectionChanged || state.myVoiceChannelId !== prev.myVoiceChannelId) schedule();
  });
  const offCall = subscribeDmCall(schedule);
  let recorderKind = getRecorder().kind;
  const offRecorder = subscribeRecorder(state => { if (state.kind !== recorderKind) { recorderKind = state.kind; schedule(); } });
  const offQueue = sendQueue.subscribeActivity(schedule);
  const offFailed = failedSends.subscribeActivity(schedule);
  const observer = new MutationObserver(schedule);
  observer.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['data-bunker-window-busy'] });
  const onMessage = (event: MessageEvent) => {
    if (event.source !== window.parent || event.origin !== host) return;
    const data = event.data;
    if (!data || data.v !== 1) return;
    if (data.type === 'cispracs:prepare-bunker-window' && typeof data.requestId === 'string' && /^[a-zA-Z0-9-]{1,80}$/.test(data.requestId)) publish(data.requestId);
    if (data.type === 'cispracs:restore-bunker-workspace') {
      clearRestore();
      restorePending = readWorkspaceSelection(data.selection);
      if (restorePending) {
        restoreDeadline = Date.now() + WORKSPACE_RESTORE_TIMEOUT_MS;
        restoreTimer = setTimeout(clearRestore, WORKSPACE_RESTORE_TIMEOUT_MS);
        restore();
      }
    }
  };
  const flushCurrent = () => { const id = useDataStore.getState().currentChannelId; if (id) flushDraft(id); };
  window.addEventListener('beforeunload', flushCurrent);
  window.addEventListener('pagehide', flushCurrent);
  window.addEventListener('message', onMessage);
  schedule();
  return () => {
    clearRestore();
    if (publishTimer !== null) clearTimeout(publishTimer);
    window.removeEventListener('beforeunload', flushCurrent); window.removeEventListener('pagehide', flushCurrent);
    offData(); offCall(); offRecorder(); offQueue(); offFailed(); observer.disconnect(); window.removeEventListener('message', onMessage);
  };
}
